/**
 * Acompanhamento do cliente na ficha da Central: pendências abertas (Resolver,
 * Descartar, Atribuir), divergências planilha × WhatsApp, decisões datadas,
 * conversas ligadas (com o atalho para abrir na tela de Conversas e criar
 * pendência a partir de uma mensagem), histórico curto e apelidos. Toda
 * pendência e decisão mostra a mensagem de origem (para quem pode ler).
 */
import React, { useEffect, useState } from "react";
import { FiMessageCircle, FiPlus, FiX } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import AsyncButton from "../../../components/AsyncButton";
import ConfirmDialog from "../../../components/ConfirmDialog";
import { useAuth } from "../../../context/AuthContext";
import {
  adicionarApelido,
  agirPendencia,
  criarPendencia,
  lerMensagens,
  listarResponsaveis,
  removerApelido,
} from "../../../services/centralProjetos";
import { formatarDataLocal, plural } from "../../../utils/dinheiro";
import { ROTULO_CLASSIFICACAO, TIPOS_PENDENCIA, quandoFoi, textoPrazo } from "../apresentacao";
import { MensagensDeOrigem } from "../comum";
import { MensagensNaCaixa } from "../Mensagens";
import {
  Acoes,
  Botao,
  BotaoDiscreto,
  Campo,
  Chip,
  Chips,
  Item,
  Linha,
  Lista,
  Ponto,
  Principal,
  Secao,
  SecaoTitulo,
  Secundario,
  Vazio,
} from "../styles";

function PendenciaAberta({ pendencia, podeTratar, responsaveis, aoMudar }) {
  const { request } = useAuth();
  const [confirmar, setConfirmar] = useState(null);

  async function fechar(estado) {
    await agirPendencia(request, pendencia.id, "fechar", { estado });
    setConfirmar(null);
    aoMudar();
  }

  async function atribuir(usuarioId) {
    await agirPendencia(request, pendencia.id, "atribuir", { usuario_id: usuarioId || null });
    aoMudar();
  }

  const tom = pendencia.tipo === "sem_resposta" || pendencia.tipo === "aguardando_equipe" ? "warning" : undefined;
  return (
    <Item>
      <Linha>
        <Principal title={pendencia.descricao} style={{ whiteSpace: "normal" }}>
          <Ponto $tom={tom} aria-hidden="true" />
          {pendencia.descricao}
        </Principal>
        <Secundario>
          {pendencia.tipo_rotulo} · {textoPrazo(pendencia.prazo)} · {pendencia.origem === "ia" ? "sugerida pela IA" : pendencia.origem === "regra" ? "regra automática" : "criada à mão"}
          {pendencia.atribuida_a ? ` · com ${pendencia.atribuida_a.nome}` : ""}
        </Secundario>
        {pendencia.resolucao_sugerida_em ? (
          <Secundario $tom="success">A conversa indica que já foi resolvida. Confira e feche.</Secundario>
        ) : null}
        <Acoes style={{ marginTop: 6 }}>
          <MensagensDeOrigem ids={pendencia.mensagens_origem} />
          {podeTratar ? (
            <>
              <Botao type="button" onClick={() => setConfirmar("resolvida")}>Resolver</Botao>
              <Botao type="button" onClick={() => setConfirmar("descartada")}>Descartar</Botao>
              <Campo as="div" style={{ minWidth: 150 }}>
                <select
                  aria-label="Atribuir a"
                  value={pendencia.atribuida_a?.id || ""}
                  onChange={(e) => atribuir(e.target.value)}
                >
                  <option value="">Sem responsável</option>
                  {responsaveis.map((p) => (
                    <option key={p.id} value={p.id}>{p.nome}</option>
                  ))}
                </select>
              </Campo>
            </>
          ) : null}
        </Acoes>
      </Linha>
      <ConfirmDialog
        open={Boolean(confirmar)}
        titulo={confirmar === "resolvida" ? "Resolver a pendência?" : "Descartar a pendência?"}
        descricao={pendencia.descricao}
        confirmarLabel={confirmar === "resolvida" ? "Resolver" : "Descartar"}
        tone={confirmar === "resolvida" ? "primary" : "danger"}
        onConfirmar={() => fechar(confirmar)}
        onCancelar={() => setConfirmar(null)}
      />
    </Item>
  );
}

export function Pendencias({ abertas, podeTratar, aoMudar }) {
  const { request } = useAuth();
  const [responsaveis, setResponsaveis] = useState([]);
  useEffect(() => {
    if (!podeTratar) return;
    listarResponsaveis(request).then((lista) => setResponsaveis(lista || [])).catch(() => setResponsaveis([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podeTratar]);
  return (
    <Secao aria-labelledby="ficha-pendencias">
      <SecaoTitulo><h2 id="ficha-pendencias">Pendências abertas</h2><span>{abertas.length}</span></SecaoTitulo>
      <Lista>
        {abertas.length === 0 ? <Vazio>Nenhuma pendência aberta.</Vazio> : null}
        {abertas.map((p) => (
          <PendenciaAberta key={p.id} pendencia={p} podeTratar={podeTratar} responsaveis={responsaveis} aoMudar={aoMudar} />
        ))}
      </Lista>
    </Secao>
  );
}

export function Decisoes({ decisoes }) {
  return (
    <Secao aria-labelledby="ficha-decisoes">
      <SecaoTitulo><h2 id="ficha-decisoes">Decisões tomadas</h2><span>{decisoes.length}</span></SecaoTitulo>
      <Lista>
        {decisoes.length === 0 ? <Vazio>Nenhuma decisão registrada.</Vazio> : null}
        {decisoes.map((d) => (
          <Item key={d.id}>
            <Linha>
              <Principal style={{ whiteSpace: "normal" }}>{d.descricao}</Principal>
              <Secundario>{formatarDataLocal(d.data)}{d.ambiente ? ` · ${d.ambiente}` : ""}</Secundario>
              <div style={{ marginTop: 4 }}><MensagensDeOrigem ids={d.mensagens_origem} /></div>
            </Linha>
          </Item>
        ))}
      </Lista>
    </Secao>
  );
}

export function Divergencias({ divergencias }) {
  if (!divergencias.length) return null;
  return (
    <Secao aria-labelledby="ficha-divergencias">
      <SecaoTitulo><h2 id="ficha-divergencias">Divergências planilha × WhatsApp</h2></SecaoTitulo>
      <Lista>
        {divergencias.map((d) => (
          <Item key={`${d.regra}-${d.ambiente}`}>
            <Linha>
              <Principal style={{ whiteSpace: "normal" }}>
                <Ponto $tom="warning" aria-hidden="true" />
                {d.ambiente ? `${d.ambiente}: ` : ""}{d.descricao}
              </Principal>
            </Linha>
          </Item>
        ))}
      </Lista>
    </Secao>
  );
}

export function Historico({ fechadas }) {
  if (!fechadas.length) return null;
  return (
    <Secao aria-labelledby="ficha-historico">
      <SecaoTitulo><h2 id="ficha-historico">Histórico</h2></SecaoTitulo>
      <Lista>
        {fechadas.map((p) => (
          <Item key={p.id}>
            <Linha>
              <Principal style={{ whiteSpace: "normal", fontWeight: 500 }}>{p.descricao}</Principal>
              <Secundario>
                {p.estado === "resolvida" ? "Resolvida" : "Descartada"} {quandoFoi(p.resolvida_em)}
                {p.observacao ? ` · ${p.observacao}` : ""}
              </Secundario>
            </Linha>
          </Item>
        ))}
      </Lista>
    </Secao>
  );
}

/** Pendência criada à mão a partir de uma mensagem (que vira a origem). */
function NovaPendencia({ pedido, mensagem, aoCriar, aoCancelar }) {
  const { request } = useAuth();
  const [descricao, setDescricao] = useState("");
  const [tipo, setTipo] = useState("aguardando_equipe");
  return (
    <div style={{ display: "grid", gap: 6, marginTop: 4 }}>
      <Campo>
        O que está pendente
        <input value={descricao} maxLength={500} onChange={(e) => setDescricao(e.target.value)} placeholder="Ex.: definir as gavetas da penteadeira" />
      </Campo>
      <Acoes>
        <Campo as="div" style={{ minWidth: 180 }}>
          <select aria-label="Tipo da pendência" value={tipo} onChange={(e) => setTipo(e.target.value)}>
            {TIPOS_PENDENCIA.map((t) => <option key={t.valor} value={t.valor}>{t.rotulo}</option>)}
          </select>
        </Campo>
        <AsyncButton
          variant="primary"
          disabled={!descricao.trim()}
          successMessage="Pendência criada."
          onClick={async () => {
            await criarPendencia(request, { numero_pedido: pedido, descricao: descricao.trim(), tipo, mensagens: [mensagem.id] });
            aoCriar();
          }}
        >
          Criar
        </AsyncButton>
        <Botao type="button" onClick={aoCancelar}>Cancelar</Botao>
      </Acoes>
    </div>
  );
}

export function Conversas({ pedido, conversas, podeLer, podeTratar, aoMudar }) {
  const navigate = useNavigate();
  const [criandoPara, setCriandoPara] = useState(null);
  const { request } = useAuth();
  const [abertas, setAbertas] = useState({});

  async function ler(id) {
    const resposta = await lerMensagens(request, { conversa: id });
    setAbertas((atual) => ({ ...atual, [id]: resposta.mensagens || [] }));
  }

  function acaoDaMensagem(mensagem) {
    if (!podeTratar) return null;
    if (criandoPara === mensagem.id) {
      return (
        <NovaPendencia
          pedido={pedido}
          mensagem={mensagem}
          aoCriar={() => { setCriandoPara(null); aoMudar(); }}
          aoCancelar={() => setCriandoPara(null)}
        />
      );
    }
    return (
      <BotaoDiscreto type="button" onClick={() => setCriandoPara(mensagem.id)}>
        <FiPlus aria-hidden="true" /> Criar pendência
      </BotaoDiscreto>
    );
  }

  return (
    <Secao aria-labelledby="ficha-conversas">
      <SecaoTitulo><h2 id="ficha-conversas">Conversas vinculadas</h2><span>{conversas.length}</span></SecaoTitulo>
      <Lista>
        {conversas.length === 0 ? <Vazio>Nenhuma conversa do WhatsApp ligada a este pedido.</Vazio> : null}
        {conversas.map((c) => (
          <Item key={c.id}>
            <Linha>
              <Principal>{c.nome} <small>· {ROTULO_CLASSIFICACAO[c.classificacao] || "A classificar"}</small></Principal>
              <Secundario>Última mensagem {quandoFoi(c.ultima_mensagem_em)} · {plural(c.mensagens_total || 0, "mensagem", "mensagens")}</Secundario>
              {c.previa?.situacao ? <Secundario $quebra>{c.previa.situacao}</Secundario> : null}
              <Acoes style={{ marginTop: 6 }}>
                <Botao type="button" onClick={() => navigate(`/central-projetos/conversas/${c.id}`)}>
                  <FiMessageCircle aria-hidden="true" /> Abrir conversa
                </Botao>
                {podeLer && !abertas[c.id] ? (
                  <AsyncButton variant="secondary" onClick={() => ler(c.id)} silentSuccess>Ler as últimas aqui</AsyncButton>
                ) : null}
              </Acoes>
              {abertas[c.id] ? (
                <div style={{ marginTop: 8 }}>
                  <MensagensNaCaixa mensagens={abertas[c.id]} mostrarAutores={Boolean(c.grupo)} acaoDaMensagem={acaoDaMensagem} />
                </div>
              ) : null}
            </Linha>
          </Item>
        ))}
      </Lista>
    </Secao>
  );
}

export function Apelidos({ pedido, apelidos, podeTratar, aoMudar }) {
  const { request } = useAuth();
  const [novo, setNovo] = useState("");
  if (!podeTratar && !apelidos.length) return null;
  return (
    <Secao aria-labelledby="ficha-apelidos">
      <SecaoTitulo><h2 id="ficha-apelidos">Apelidos</h2><span>outros nomes do cliente no WhatsApp</span></SecaoTitulo>
      <Chips>
        {apelidos.map((a) => (
          <Chip key={a.id} as="span" $ativo>
            {a.apelido}
            {podeTratar ? (
              <button
                type="button"
                aria-label={`Remover ${a.apelido}`}
                style={{ border: 0, background: "none", marginLeft: 4, cursor: "pointer" }}
                onClick={async () => { await removerApelido(request, pedido, a.id); aoMudar(); }}
              >
                <FiX aria-hidden="true" />
              </button>
            ) : null}
          </Chip>
        ))}
      </Chips>
      {podeTratar ? (
        <Acoes>
          <Campo as="div" style={{ flex: "1 1 200px" }}>
            <input aria-label="Novo apelido" placeholder="Ex.: Borboleta" value={novo} onChange={(e) => setNovo(e.target.value)} />
          </Campo>
          <AsyncButton
            variant="secondary"
            disabled={!novo.trim()}
            successMessage="Apelido guardado."
            onClick={async () => { await adicionarApelido(request, pedido, novo.trim()); setNovo(""); aoMudar(); }}
          >
            Adicionar
          </AsyncButton>
        </Acoes>
      ) : null}
    </Secao>
  );
}
